/**
 * D-196: the Shop tab's quiet dot. It shows while something went on sale after you last opened the Shop (within the
 * D-194 "new" window), or while the live occasion's pack is yours to claim. Opening the Shop marks it seen on this device.
 */
import { useEffect, useState } from "react";
import { SHOP_NEW_MS } from "@digu/shared";
import { useSession } from "./session";
import { occasionsState, useOccasionsTick } from "./occasions";

const KEY = "digu.shopSeenAt";
const EVENT = "digu:shop-seen";

function seenAt(): number {
  try { return Number(localStorage.getItem(KEY) ?? 0) || 0; } catch { return 0; }
}
/** Called when the Shop opens. */
export function markShopSeen(now = Date.now()): void {
  try { localStorage.setItem(KEY, String(now)); } catch { /* private mode: the dot just stays for this visit */ }
  window.dispatchEvent(new Event(EVENT));
}

export function shopHasNew(o: { shopNewAt: number | null; seenAt: number; now: number; occasionToClaim: boolean }): boolean {
  if (o.occasionToClaim) return true;
  if (o.shopNewAt === null) return false;
  // An item dated ahead (a pack that goes on sale at midnight) counts as new from now; opening the Shop sees it.
  const at = Math.min(o.shopNewAt, o.now);
  return at > o.seenAt && o.now - at < SHOP_NEW_MS;
}

export function useShopNew(): boolean {
  const { shopNewAt } = useSession();
  useOccasionsTick();
  const [seen, setSeen] = useState(seenAt);
  useEffect(() => {
    const on = () => setSeen(seenAt());
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  const cur = occasionsState().current;
  const occasionToClaim = !!cur && cur.live && !!cur.mine && !cur.mine.claimed && cur.mine.canClaim;
  return shopHasNew({ shopNewAt, seenAt: seen, now: Date.now(), occasionToClaim });
}
